import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import Collapse from '@material-ui/core/Collapse';
import { faChevronDown, faChevronUp, faChevronRight } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { breakpoints, isMobile as isMobileUtil } from '@sorted/sorted-ui';
import { useLocation } from 'react-router-dom';
import colors from '../colors';
import ListWrapper from './ListWrapper';

const Title = styled.div`
	align-items: center;
	background-color: ${({ isOpen }) =>
		isOpen ? colors.black : 'transparent'};
	display: flex;
	height: 70px;
	padding-left: 20px;
	padding-right: 18px;
	transition: background-color 0.1s ease, color 0.1s ease;

	@media (min-width: ${breakpoints.smallDesktop}) {
		height: 90px;
		padding-right: 3px;
	}
`;

const ExpandCollapseIcon = styled(FontAwesomeIcon)`
	color: ${({ $isOpen }) => ($isOpen ? colors.pink : colors.white)};
	@media (min-width: ${breakpoints.tablet}) {
		transform: rotate(0deg);
	}
`;

const MenuItemIcon = styled.div`
	display: flex;
	align-items: center;
	min-width: 36px;
	color: ${({ isOpen }) => (isOpen ? colors.pink : colors.white)};
`;

const ItemTitle = styled.span`
	display: block;
	margin: 4px 0;
	whitespace: no-wrap;
	flex: 1 1 auto;

	@media (min-width: ${breakpoints.smallDesktop}) {
		display: ${({ collapse }) => (collapse ? 'none' : 'block')};
		min-width: 120px;
	}
`;

const CollapsingMenu = ({
	title,
	direction = 'down',
	Icon,
	onClick,
	open,
	isCollapsed = false,
	children,
	...props
}) => {
	const [isOpenDesktop, setOpenDesktop] = useState(false);
	const location = useLocation();
	const isMobile = isMobileUtil();

	const titleBlock = (
		<Title isOpen={isOpenDesktop}>
			<MenuItemIcon isOpen={(isMobile && open) || (!isMobile && isOpenDesktop)}>
				<Icon />
			</MenuItemIcon>

			<ItemTitle collapse={isCollapsed}>{title}</ItemTitle>

			{children && (
				<ExpandCollapseIcon
					$isOpen={(isMobile && open) || (!isMobile && isOpenDesktop)}
					icon={(isMobile && open && faChevronUp) || (isMobile && !open && faChevronDown) || faChevronRight}
					size="sm"
					fixedWidth
				/>
			)}
		</Title>
	);

	useEffect(() => {
		setOpenDesktop(false);
	}, [location]);

	return (
		<ListWrapper
			direction={direction}
			onMouseOver={() => {
				setOpenDesktop(true);
			}}
			onMouseOut={() => {
				setOpenDesktop(false);
			}}
			onClick={() => isMobile && onClick(title)}
			{...props}
		>
			{(isMobile || direction === 'down') && titleBlock}
			<Collapse
				in={isMobile ? open : isOpenDesktop}
				timeout={isMobile ? 'auto' : 0}
			>
				<ListWrapper direction={direction} subMenu>
					{children}
				</ListWrapper>
			</Collapse>
			{!isMobile && direction === 'up' && titleBlock}
		</ListWrapper>
	);
};

CollapsingMenu.propTypes = {
	direction: PropTypes.string,
	Icon: PropTypes.oneOfType([
		PropTypes.element,
		PropTypes.elementType,
		PropTypes.node
	]),
	isCollapsed: PropTypes.bool,
	onClick: PropTypes.func.isRequired,
	open: PropTypes.bool,
	title: PropTypes.string.isRequired
};

CollapsingMenu.defaultProps = {
	direction: 'down',
	Icon: () => {},
	isCollapsed: false,
	open: false
};

export default CollapsingMenu;
